import dayjs from "dayjs";
import * as uuid from "uuid";
import base64 from "base-64";
import {
  currentFeatures,
  endpoints,
  oids,
  roles as roleCodes,
} from "../../configs/Settings";
import type { Task } from "fhir/r5";

import {
  IAppointmentData,
  IEmail,
  IEncounterData,
  IPatch,
  IPatientData,
  IPractitionerData,
  IPractitionerRoleData,
  IRelatedPersonData,
  ISlotData,
  ITaskData,
  IVetPatientData,
} from "../../utils/interfaces";
import { useFhirData } from "../../utils/useFhirData";
import { useFormatter } from "../../utils/useFormatter";

import { AffiliationReferences } from "./organizationaffiliation/PutOrganizationAffiliation";
import { CoverageFormData } from "@/pages/patientinsurance/components/CoverageModal";
import { AffiliationFormData } from "@/pages/manageplans/components/AffiliationModal";

function Practitioner(objData: IPractitionerData) {
  return JSON.stringify({
    resourceType: "Practitioner",
    id: `${oids.cpf}-${objData.identifier}`,
    identifier: [
      {
        system: `urn:oid:${oids.cpf}`,
        value: objData.identifier,
      },
    ],
    birthDate: objData.birthDate,
    name: [{ text: objData.name }],
  });
}

function PersonAux(objData: any) {
  return JSON.stringify({
    resourceType: "Person",
    id: `${oids.cpf}-${objData.identifier}`,
    identifier: [
      {
        system: `urn:oid:${oids.cpf}`,
        value: objData.identifier,
      },
    ],
    active: objData?.active || true,
    name: [{ text: objData.name }],
    birthDate: objData.birthDate,
    extension: [
      {
        url: "https://fluxmed.com.br/termRefused",
        valueBoolean: objData.termRefused || false,
      },
    ],
  });
}

function PractitionerRole(objData: IPractitionerRoleData) {
  const organizationId = objData.organizationId.split("-")?.[1];
  const cpf = objData.practitionerId.split("-")[1];

  // Extensão para suportar "associação" (agrupamento por CNPJ e permissão de sócio).
  // Mantemos o `organization` original (host) e guardamos o vínculo associativo aqui
  // para não quebrar integrações existentes que dependem do id/reference atual.
  const associationCnpjDigits = String(
    objData?.associationCnpjDigits || "",
  ).replace(/\D/g, "");
  const associationOrganizationAlias = String(
    objData?.associationOrganizationAlias || "",
  ).trim();
  const associationIsSocio = objData?.associationIsSocio === true;

  const associationExtension =
    associationCnpjDigits && associationOrganizationAlias
      ? [
          {
            url: "https://fluxmed.cloud/StructureDefinition/PractitionerRoleAssociation",
            extension: [
              { url: "cnpj", valueString: associationCnpjDigits },
              {
                url: "organizationAlias",
                valueString: associationOrganizationAlias,
              },
              { url: "isSocio", valueBoolean: associationIsSocio },
            ],
          },
        ]
      : undefined;

  // Role automática para marcar o "sócio" da associação.
  // O UI já consegue exibir via `useDict.roles["associated"]` (mantido separado de owner/manager).
  const associationSocioCode = associationIsSocio
    ? [
        {
          coding: [
            {
              system: "associated",
              code: "RL_ASSOCIATED",
            },
          ],
        },
      ]
    : [];

  return JSON.stringify({
    resourceType: "PractitionerRole",
    id: `${organizationId}-${oids.cpf}-${cpf}`,
    practitioner: {
      reference: `Practitioner/${objData.practitionerId}`,
      display: objData.practitionerDisplay,
    },
    organization: {
      reference: `Organization/${objData.organizationId}`,
      display: objData.organizationDisplay,
    },
    ...(associationExtension && { extension: associationExtension }),
    code: objData?.invite
      ? [
          {
            coding: [
              {
                system: "invited",
                code: "RL_INVITED",
              },
            ],
          },
          {
            ...(objData?.role?.includes("practitioner") && {
              coding: [
                {
                  system: "practitioner",
                  code: "RL_PRACTITIONER",
                },
              ],
            }),
          },
          {
            ...(objData?.role?.includes("manager") && {
              coding: [
                {
                  system: "manager",
                  code: "RL_MANAGER",
                },
              ],
            }),
          },
          {
            ...(objData?.role?.includes("owner") && {
              coding: [
                {
                  system: "owner",
                  code: "RL_OWNER",
                },
              ],
            }),
          },
          ...associationSocioCode,
        ]
      : [
          {
            coding: [
              {
                system: "owner",
                code: "RL_OWNER",
              },
            ],
          },
          {
            coding: [
              {
                system: "manager",
                code: "RL_MANAGER",
              },
            ],
          },
        ],
  });
}

/**
 * Monta o array `code` para PATCH de PractitionerRole.
 * Quando `preserveAssociated` é true, reapendamos RL_ASSOCIATED (system "associated") para não
 * remover o vínculo de sócio/associação ao editar apenas owner/manager/practitioner no diálogo.
 */
function PatchPractitionerRole(
  rolesSelected: any,
  options?: { preserveAssociated?: boolean },
) {
  const owner = {
    coding: [
      {
        system: "owner",
        code: "RL_OWNER",
      },
    ],
  };
  const manager = {
    coding: [
      {
        system: "manager",
        code: "RL_MANAGER",
      },
    ],
  };
  const practitioner = {
    coding: [
      {
        system: "practitioner",
        code: "RL_PRACTITIONER",
      },
    ],
  };

  const updatedRoles = [owner, manager, practitioner].filter(
    (el: fhir5.CodeableConcept) =>
      rolesSelected.some((value: any) =>
        el?.coding?.[0]?.system?.includes(value),
      ),
  );

  if (options?.preserveAssociated) {
    updatedRoles.push({
      coding: [
        {
          system: "associated",
          code: roleCodes.Associated,
        },
      ],
    });
  }

  return updatedRoles;
}

/**
 * Constrói o recurso FHIR R5 Location.
 *
 * Tipos de atendimento suportados via extensões:
 *  - presencial: endereço físico obrigatório; virtual-service=false
 *  - teleatendimento: virtual-service=true; sem endereço físico
 *  - chat: virtual-service=true; sem endereço físico; canal=chat
 *  - domicilio: endereço será preenchido depois; SEM virtual-service; canal=domicilio
 *
 * A extension "attendance-channel" é adicionada para todos os tipos,
 * permitindo diferenciar teleatendimento de chat e de domicílio.
 * Localizações antigas sem essa extension são consideradas teleatendimento
 * por retrocompatibilidade.
 *
 * IMPORTANTE: domicílio NÃO recebe a extension virtual-service, pois
 * não é um atendimento virtual mas também não é presencial no estabelecimento.
 */
function Location(objData: any, access: any, uuid?: string) {
  // Tipos virtuais (sem endereço físico)
  const isVirtual =
    objData.serviceOnline === true ||
    objData.attendanceType === "teleatendimento" ||
    objData.attendanceType === "chat";

  // Domicílio também não tem endereço físico na criação (será preenchido depois)
  const isDomicilio = objData.attendanceType === "domicilio";

  // Inclui endereço somente para atendimento presencial (ou quando há cidade preenchida)
  const includeAddress = !isVirtual && !isDomicilio && objData.city;

  // Domicílio não recebe a extension virtual-service (não é virtual, nem presencial no estabelecimento)
  // Presencial e tele/chat mantêm a extension por retrocompatibilidade
  const virtualServiceExtension = !isDomicilio
    ? [
        {
          // Extension legada: mantida para retrocompatibilidade em presencial e virtual
          url: "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service",
          valueBoolean: isVirtual,
        },
      ]
    : [];

  return JSON.stringify({
    id: uuid,
    resourceType: "Location",
    status: "active",
    name: objData.name,
    alias: objData.name,
    // Endereço físico: incluído apenas para presencial com cidade informada
    ...(includeAddress && {
      address: {
        city: objData.city,
        state: objData.state,
        postalCode: objData.cep,
        country: "Brasil",
        line: [
          objData.city && `${objData.city}`,
          objData.state && `${objData.state}`,
          objData.cep && `${objData.cep}`,
          objData.number && ` ${objData.number}`,
          objData.street && ` ${objData.street}`,
          objData.complement && ` ${objData.complement}`,
        ],
      },
    }),
    ...(objData?.phoneClinic && {
      contact: [
        {
          telecom: [
            {
              system: "phone",
              value: objData?.phoneClinic,
            },
          ],
        },
      ],
    }),
    managingOrganization: {
      reference: `Organization/${oids.cnpj}-${access}`,
    },
    extension: [
      // Extension virtual-service: incluída apenas para presencial e virtual (não para domicílio)
      ...virtualServiceExtension,
      {
        // Extension de canal de atendimento: identifica o tipo para tele, chat, domicílio e presencial
        url: "https://fluxmed.com.br/fhir/StructureDefinition/attendance-channel",
        valueCode: objData.attendanceType || "presencial",
      },
    ],
  });
}

function Slot(objData: ISlotData, uuid?: string) {
  return JSON.stringify({
    id: uuid,
    resourceType: "Slot",
    appointmentType: {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/v2-0276",
          version: "2.9",
          code: objData?.type,
        },
      ],
      text: objData?.type,
    },
    schedule: {
      reference: `Schedule/${objData?.scheduleId}`,
    },
    status: "free",
    start: objData.start,
    end: objData.end,
    overbooked: false,
  });
}

function OrganizationAffiliation(
  objData: AffiliationFormData,
  references: AffiliationReferences,
) {
  const specialties = objData.procedures?.map((procedure) => {
    return {
      extension: [
        {
          url: "https://fluxmed.com.br/fhir/StructureDefinition/service-value",
          valueMoney: {
            value: parseFloat(
              useFormatter?.extractNumericValue(
                procedure.serviceCost?.toString() || "0",
              ),
            ),
            currency: "BRL",
          },
        },
      ],
      coding: [
        {
          system:
            "https://fluxmed.com.br/fhir/r5/CodeSystem/TUSSProcedimentosEventos",
          code: procedure.code,
          display: procedure.display,
        },
      ],
    };
  });

  return {
    resourceType: "OrganizationAffiliation",
    active: true,
    identifier: [
      {
        system: "cadastroInstituicao",
        value: objData.institutionRegistration,
      },
    ],
    extension: [
      {
        url: "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation",
        valueReference: {
          reference: `InsurancePlan/${objData.insurancePlans?.value}`,
          display: objData.insurancePlans?.display,
        },
      },
    ],
    organization: {
      reference: `Organization/${references.prestadorId}`,
      display: references.prestadorDisplay,
    },
    participatingOrganization: {
      reference: `Organization/${objData.organization?.value}`,
      display: objData.organization?.label,
    },
    code: [
      {
        coding: [
          {
            system: "http://terminology.hl7.org/CodeSystem/organization-role",
            code: "provider",
            display: "Healthcare Provider",
          },
        ],
      },
    ],
    specialty: specialties,
  };
}

function Coverage(
  objData: CoverageFormData,
  references: { patientId: string },
) {
  return JSON.stringify({
    resourceType: "Coverage",
    identifier: [
      {
        type: {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/v2-0203",
              code: "MB",
              display: "Member Number",
            },
          ],
        },
        system: "http://fluxmed.com.br/beneficiarios",
        value: objData.cardNumber,
      },
    ],
    status: "active",
    beneficiary: {
      reference: `Patient/${oids.cpf}-${references.patientId}`,
    },
    insurer: {
      reference: `Organization/${objData.organization?.value}`,
      display: objData.organization?.label,
    },
    insurancePlan: {
      reference: `InsurancePlan/${objData.insurancePlan?.value}`,
      display: objData.insurancePlan?.display,
    },
  });
}

function HealthcareService(objData: any, access: any, uuid?: string) {
  const auxTimeAvailable = objData?.timeAvailable.map((timeAvailable: any) => {
    const availableInterval = objData.daysOfWeek?.map((day: any) => {
      return {
        daysOfWeek: timeAvailable[day] && useFormatter.translateWeekDays(day),
        availableStartTime: useFormatter.formatTime(
          timeAvailable[day]?.timeStart,
        ),
        availableEndTime: useFormatter.formatTime(timeAvailable[day]?.timeEnd),
      };
    });

    const cleanInterval = availableInterval.filter(
      (interval: any) => interval?.daysOfWeek !== undefined,
    );

    const groupInterval: { [key: string]: boolean } = {};

    const availableObjects: any[] = [];

    cleanInterval.forEach(
      (obj: {
        availableStartTime: any;
        availableEndTime: any;
        daysOfWeek: any;
      }) => {
        const timeValues = `${obj.availableStartTime}-${obj.availableEndTime}`;

        if (!groupInterval[timeValues]) {
          groupInterval[timeValues] = true;
          availableObjects.push({
            availableStartTime: obj.availableStartTime,
            availableEndTime: obj.availableEndTime,
            daysOfWeek: [obj.daysOfWeek],
          });
        } else {
          const sameInterval = availableObjects.find(
            (item: any) =>
              item.availableStartTime === obj.availableStartTime &&
              item.availableEndTime === obj.availableEndTime,
          );
          sameInterval.daysOfWeek.push(obj.daysOfWeek);
        }
      },
    );
    return availableObjects;
  });

  let mergeTime = auxTimeAvailable[0].concat(auxTimeAvailable[1]);

  return JSON.stringify({
    id: uuid,
    resourceType: "HealthcareService",
    providedBy: {
      reference: `Organization/${oids.cnpj}-${access}`,
    },
    name: objData.healthService,
    appointmentRequired: true,
    availability: { availableTime: mergeTime },
    //R4 Version availableTime: mergeTime,
  });
}
function Schedule(objData: any, reference: any, uuid?: string) {
  const actors = [
    {
      reference: `Location/${reference.locationReference.id}`,
      display: reference.locationReference.name,
    },
    {
      reference: `HealthcareService/${reference.healthcareReference.id}`,
      display: reference.healthcareReference.name,
    },
    {
      reference: reference.practitionerReference?.value,
      display: reference.practitionerReference?.label,
    },
  ];

  const auxPersonTime =
    parseInt(dayjs(objData.personDurationTime).format("HH:mm").split(":")[0]) *
      60 +
    parseInt(dayjs(objData.personDurationTime).format("HH:mm").split(":")[1]);

  const formatDurationTime =
    objData.durationTimeDefault === "personDuration"
      ? auxPersonTime
      : objData.durationTimeDefault === "01:00"
        ? 60
        : parseInt(objData.durationTimeDefault.split(":")[1]);

  return JSON.stringify({
    id: uuid,
    resourceType: "Schedule",
    name: objData.scheduleName,
    active: true,
    actor: actors,
    planningHorizon: {
      start: objData.dataStart
        .toISOString()
        .split("T")[0]
        .concat("T00:00:00.000"),
      end: objData.dataEnd.toISOString().split("T")[0].concat("T23:59:59.999"),
    },
    extension: [
      {
        url: "https://fluxmed.com.br/fhir/StructureDefinition/schedule-slot-duration",
        valueDuration: {
          value: `${formatDurationTime}`,
          unit: "minutes",
          system: "http://unitsofmeasure.org",
          code: "min",
        },
      },
      {
        url: "https://fluxmed.com.br/fhir/StructureDefinition/schedule-slot-anticipation-period",
        valueDuration: {
          value: `${objData.advanceWarning || 15}`,
          unit: "days",
          system: "http://unitsofmeasure.org",
          code: "d",
        },
      },
      ...(parseFloat(useFormatter?.extractNumericValue(objData.serviceCost)) &&
      parseFloat(useFormatter?.extractNumericValue(objData.serviceCost)) > 0
        ? [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/service-cost",
              valueDecimal: parseFloat(
                useFormatter?.extractNumericValue(objData.serviceCost),
              ),
            },
          ]
        : []),
      ...(objData.returnDays && Number(objData.returnDays) > 0
        ? [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/schedule-return-days",
              valueDuration: {
                value: Number(objData.returnDays),
                unit: "days",
                system: "http://unitsofmeasure.org",
                code: "d",
              },
            },
          ]
        : []),
      // Extensão opcional de exposição da agenda.
      // Quando o usuário marca a opção de exposição no formulário,
      // criamos a extensão com valueBoolean true. Caso contrário,
      // não incluímos a extensão (agenda permanece privada por padrão).
      ...(objData.scheduleExposureEnabled
        ? [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/schedule-exposure",
              valueBoolean: true,
            },
          ]
        : [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/schedule-exposure",
              valueBoolean: false,
            },
          ]),
      // Extensão de tipo de atendimento do schedule.
      // Identifica o canal/modalidade desta agenda: presencial | teleatendimento | chat | domicilio
      ...(objData.attendanceType
        ? [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/schedule-attendance-type",
              valueCode: objData.attendanceType,
            },
          ]
        : []),
    ],
  });
}

function Patient(objData: IPatientData) {
  return JSON.stringify({
    resourceType: "Patient",
    identifier: [
      {
        use: "official",
        type: {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/v2-0203",
              code: "TAX",
            },
          ],
        },
        system: `urn:oid:${oids.cpf}`,
        value: objData.identifier,
      },
    ],
    active: true,
    name: [
      {
        use: "official",
        text: useFormatter.fixName(objData.name.toUpperCase()),
      },
      {
        use: "usual",
      },
    ],
    telecom: [
      {
        system: "phone",
        value: objData?.phoneNumber,
      },
    ],
    gender: objData.gender,
    birthDate: objData.birthDate,
    deceasedBoolean: false,
    multipleBirthBoolean: false,
    ...(objData.link && { link: objData.link }),
  });
}

function VetPatient(objData: IVetPatientData) {
  let photoAttachment: any = null;

  // Lógica simplificada para tratar apenas base64 (PNG ou JPG), como solicitado.
  if (objData.photo) {
    let base64Data = objData.photo;
    let contentType = "image/png"; // Padrão, pois é gerado pelo cropper.

    if (base64Data.startsWith("data:")) {
      const parts = base64Data.split(",");
      const meta = parts[0].split(";")[0].split(":")[1];
      if (meta === "image/jpeg" || meta === "image/png") {
        contentType = meta;
      }
      // Pega apenas a parte do base64
      base64Data = parts[1];
    }

    photoAttachment = {
      contentType: contentType,
      data: base64Data,
    };
  }

  return JSON.stringify({
    resourceType: "Patient",
    active: true,
    ...(objData.id && { id: objData.id }),
    // Usa o anexo de foto preparado
    ...(photoAttachment && { photo: [photoAttachment] }),
    ...(objData.identifier && { identifier: objData.identifier }),
    ...(objData.speciesCode &&
      objData.speciesDisplay && {
        extension: [
          {
            url: "http://hl7.org/fhir/StructureDefinition/patient-animal",
            extension: [
              // Extensão de species (espécie)
              ...(objData.speciesCode && objData.speciesDisplay
                ? [
                    {
                      url: "species",
                      valueCodeableConcept: {
                        coding: [
                          {
                            system:
                              "http://fluxmed.com.br/fhir/CodeSystem/especie-animal",
                            code: objData.speciesCode,
                            display: objData.speciesDisplay,
                          },
                        ],
                      },
                    },
                  ]
                : []),
              // Extensão de breed (raça)
              ...(objData.breedCode && objData.breedDisplay
                ? [
                    {
                      url: "breed",
                      valueCodeableConcept: {
                        coding: [
                          {
                            system:
                              "http://fluxmed.cloud/fhir/CodeSystem/raca-animal",
                            code: objData.breedCode,
                            display: objData.breedDisplay,
                          },
                        ],
                      },
                    },
                  ]
                : []),
              // Extensão de genderStatus (castrado/não castrado)
              ...(objData.genderStatusCode && objData.genderStatusDisplay
                ? [
                    {
                      url: "genderStatus",
                      valueCodeableConcept: {
                        coding: [
                          {
                            system: "http://hl7.org/fhir/animal-genderstatus",
                            code: objData.genderStatusCode,
                            display: objData.genderStatusDisplay,
                          },
                        ],
                      },
                    },
                  ]
                : []),
              // Extensão de cor (dentro da extensão patient-animal)
              ...(objData.colorCode && objData.colorDisplay
                ? [
                    {
                      url: "animalColor",
                      valueCodeableConcept: {
                        coding: [
                          {
                            system:
                              "http://fluxmed.com.br/fhir/CodeSystem/animal-color",
                            code: objData.colorCode,
                            display: objData.colorDisplay,
                          },
                        ],
                      },
                    },
                  ]
                : []),
            ],
          },
        ],
      }),
    ...(objData.name && {
      name: [
        {
          use: "usual",
          text: objData.name,
        },
      ],
    }),
    ...(objData.gender && { gender: objData.gender }),
    ...(objData.birthDate && {
      birthDate:
        typeof objData.birthDate === "string"
          ? (objData.birthDate as string).split("T")[0]
          : (objData.birthDate as any)?.format?.("YYYY-MM-DD") ||
            objData.birthDate,
    }),
    ...((objData.city || objData.state) && {
      address: [
        {
          use: "home",
          ...(objData.city && { city: objData.city }),
          ...(objData.state && { state: objData.state }),
        },
      ],
    }),
  });
}

function RelatedPerson(
  objData: IRelatedPersonData,
  patientActor: { reference: string; display: string },
  status?: true | false,
) {
  return JSON.stringify({
    resourceType: "RelatedPerson",
    active: status || false,
    patient: {
      display: patientActor?.display,
      reference: patientActor?.reference,
    },
    relationship: [
      {
        text: objData?.relationship,
      },
    ],
    name: [
      {
        use: "official",
        text: objData?.ownerName,
      },
    ],
    identifier: [
      {
        system: oids.cpf,
        value: objData?.cpfResponsible?.replace(/[.-]/g, ""),
      },
    ],
    telecom: [
      {
        system: "phone",
        value: `${objData?.phoneNumber?.replace(/[^0-9]/g, "")}`,
      },
    ],
    period: {
      start: dayjs().toISOString(),
    },
    gender: objData?.gender,
  });
}

function Appointment(objData: IAppointmentData) {
  // Para defaultApp (app de saúde humana): usa o formato com identifier
  // Para vet: usa o reference direto (Patient/ID)

  let patientReference: string;

  if (currentFeatures?.defaultApp) {
    // App Humano: extrai o CPF e monta o formato com identifier
    // Exemplo: "Patient/2.16.840.1.113883.13.237-06610657157" → split por "-" pega "06610657157"
    const patientId = objData?.patientActor?.reference?.split("-")?.[1];
    patientReference = `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`;
  } else {
    // App Vet: usa a referência direta que já vem correta
    // Exemplo: "Patient/RAHE7F02E0-DF" → mantém como está
    patientReference = objData?.patientActor?.reference;
  }

  return JSON.stringify({
    resourceType: "Appointment",
    status:
      objData?.appointmentStatus ||
      (!objData?.appointmentIsFree ? "pending" : "booked"),
    priority: 0,
    slot: [
      {
        reference: `Slot/${objData.slotInfo.id}`,
      },
    ],
    start: objData.slotInfo.start,
    end: objData.slotInfo.end,
    appointmentType: {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/v2-0276",
          version: "2.9",
          code: objData.typeService,
        },
      ],
      // Label amigável para Pronto Atendimento; demais tipos mantêm o code
      text:
        objData.typeService === "prontoatendimento"
          ? "Pronto Atendimento"
          : objData.typeService,
    },
    ...(objData?.virtualService && {
      virtualService: {
        addressUrl: objData?.virtualService?.[0]?.addressUrl,
        sessionKey: objData?.virtualService?.[0]?.sessionKey,
      },
    }),
    participant: [
      {
        actor: {
          reference: patientReference,
          display: objData.patientActor.display,
        },
      },
      {
        actor: {
          reference: objData.locationActor.reference,
          display: objData.locationActor.display,
        },
      },
      // HealthcareService opcional — agendas sem HCS (legado) continuam válidas
      ...(objData.healthCareServiceActor?.reference
        ? [
            {
              actor: {
                reference: objData.healthCareServiceActor.reference,
                display: objData.healthCareServiceActor.display,
              },
            },
          ]
        : []),
      {
        actor: {
          reference: objData.professionalActor.reference,
          display: objData.professionalActor.display,
        },
      },
    ],
    ...(objData?.supportingInformation?.value &&
      objData?.supportingInformation?.label && {
        supportingInformation: [
          {
            reference: `OrganizationAffiliation/${objData.supportingInformation.value}`,
            display: objData.supportingInformation.label,
          },
        ],
      }),
    // Extension de endereço para agendamentos de domicílio.
    // Persiste o endereço do atendimento como valueAddress no Appointment FHIR.
    ...((objData as any)?.domicilioAddress?.city ||
    (objData as any)?.domicilioAddress?.street
      ? {
          extension: [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/home-visit-address",
              valueAddress: {
                use: "home",
                country: "Brasil",
                postalCode:
                  (objData as any).domicilioAddress?.cep?.replace(/\D/g, "") ||
                  "",
                city: (objData as any).domicilioAddress?.city || "",
                state: (objData as any).domicilioAddress?.state || "",
                district: (objData as any).domicilioAddress?.zone || "",
                line: [
                  (objData as any).domicilioAddress?.street || "",
                  (objData as any).domicilioAddress?.number || "",
                  (objData as any).domicilioAddress?.complement || "",
                ].filter(Boolean),
              },
            },
          ],
        }
      : {}),
  });
}

function Encounter(objData: IEncounterData) {
  const patient = useFhirData.findParticipant(objData?.participant, "Patient");
  const patientId = currentFeatures.defaultApp
    ? `${patient?.reference?.split("-")?.[1]}`
    : patient?.reference?.split("/")[1];

  const patientReference = currentFeatures.defaultApp
    ? `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`
    : `Patient/${patientId}`;

  const healthcareService = useFhirData.findParticipant(
    objData?.participant,
    "HealthcareService",
  );
  const location = useFhirData.findParticipant(
    objData?.participant,
    "Location",
  );

  return JSON.stringify({
    resourceType: "Encounter",
    identifier: [
      {
        system: "urn:ietf:rfc:3986",
        value: `${uuid.v1()}`,
      },
    ],
    status: "in-progress",
    // Classe do encounter conforme tipo de atendimento (FHIR R5 Encounter.class — v3-ActCode):
    //   VR = Virtual (teleatendimento, chat)
    //   HH = Home Health (atendimento a domicílio)
    //   AMB = Ambulatório (presencial) — não incluído se não informado
    ...(objData.attendanceClass && {
      class: [
        {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
              code: objData.attendanceClass,
            },
          ],
        },
      ],
    }),
    type: [
      {
        coding: [
          {
            system: "http://terminology.hl7.org/CodeSystem/v2-0276",
            version: "2.9",
            code: objData?.type,
          },
        ],
        text: objData?.type,
      },
    ],
    subject: {
      reference: patientReference,
    },
    serviceType: {
      reference: healthcareService,
    },
    appointment: {
      reference: `Appointment/${objData.appointment}`,
    },
    participant: [
      {
        actor: {
          reference: patientReference,
          display: patient?.display,
        },
      },
      objData.participant?.find((el: any) =>
        el.actor.reference.includes("Practitioner"),
      ),
    ],
    actualPeriod: {
      start: dayjs().toISOString(),
    },
    serviceProvider: {
      reference: `Organization/${objData.organization}`,
    },
    location: {
      reference: location,
    },
    // Extension de endereço para atendimentos a domicílio.
    // Espelha a mesma extension home-visit-address presente no Appointment.
    ...(objData.domicilioAddress?.city &&
    objData.domicilioAddress?.state &&
    objData.domicilioAddress?.postalCode
      ? {
          extension: [
            {
              url: "https://fluxmed.com.br/fhir/StructureDefinition/home-visit-address",
              valueAddress: {
                use: "home",
                country: "Brasil",
                postalCode: objData.domicilioAddress?.postalCode || "",
                city: objData.domicilioAddress?.city || "",
                state: objData.domicilioAddress?.state || "",
                district: objData.domicilioAddress?.district || "",
                line: (objData.domicilioAddress?.line ?? []).filter(Boolean),
              },
            },
          ],
        }
      : {}),
  });
}

function AllergyIntolerance(objData: any, encounter: any) {
  const patientId = encounter.subject;
  return JSON.stringify({
    resourceType: "AllergyIntolerance",
    category: [`${objData?.category?.value}`],
    criticality: objData?.criticality?.value,
    ...((objData.description || objData?.note) && {
      reaction: [
        {
          description: objData?.descripto,
          note: {
            text: objData?.note,
          },
        },
      ],
    }),
    clinicalStatus: {
      coding: [{ code: objData?.clinicalStatus?.value }],
    },
    verificationStatus: {
      coding: [{ code: objData?.verificationStatus?.value }],
    },
    code: {
      text: objData?.substance,
    },
    patient: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    participant: [
      {
        function: {
          coding: [{ code: "informant" }],
        },
        actor: {
          reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
        },
      },
      {
        function: {
          coding: [{ code: "enterer" }],
        },
        actor: {
          reference: `Practitioner/${oids.cpf}-${objData.practitionerId}`,
          display: objData?.practitionerName,
        },
      },
    ],
    encounter: {
      reference: `Encounter/${encounter.id}`,
    },
    recordedDate: dayjs().toISOString(),
  });
}

function ProtocolResponse({
  id,
  objData,
  references,
}: {
  objData: any;
  references?: any;
  id?: string;
}) {
  const medicationAwnser = objData?.medicamentos?.map((medicamento: any) => {
    const answers = [
      medicamento.name && {
        linkId: "8543714373691",
        answer: [{ valueString: medicamento.name }],
      },
      medicamento.type && {
        linkId: "2794386593077",
        answer: [{ valueString: medicamento.type }],
      },

      medicamento.inputValue?.code && {
        linkId: "2043756634085",
        answer: [
          { valueString: medicamento.inputValue?.code?.coding?.[0]?.display },
        ],
      },
      medicamento.inputValue?.value && {
        linkId: "5252423656882",
        answer: [{ valueString: medicamento.inputValue.value }],
      },
      medicamento.inputValue?.presentation && {
        linkId: "6273182343815",
        answer: [{ valueString: medicamento.inputValue.presentation }],
      },
      medicamento.tag && {
        linkId: "7570117070815",
        answer: [{ valueString: medicamento.tag }],
      },
      medicamento.note && {
        linkId: "5201559249537",
        answer: [{ valueString: medicamento.note }],
      },
      // Adicionamos as entradas de resposta restantes se os valores não forem nulos
      medicamento.formValue?.label
        ? {
            linkId: "4027816497232",
            answer: [{ valueString: medicamento.formValue.label }],
          }
        : null,
      medicamento.quantity
        ? {
            linkId: "4145930684259",
            answer: [
              {
                valueString: medicamento.quantity.label || medicamento.quantity,
              },
            ],
          }
        : null,
      medicamento.frequency?.value
        ? {
            linkId: "1265328124778",
            answer: [{ valueString: medicamento.frequency?.value }],
          }
        : null,
      medicamento?.time?.value
        ? {
            linkId: "6391319168670",
            answer: [{ valueString: medicamento.time?.value }],
          }
        : null,
    ].filter(Boolean); // Removemos valores nulos do array
    return {
      linkId: "2631266001552",
      item: answers,
    };
  });

  const serviceAnswer = objData?.exames?.map((exame: any) => {
    const answers = [
      exame.name && {
        linkId: "8772001052232",
        answer: [{ valueString: exame.name }],
      },
      exame?.inputValue?.code && {
        linkId: "379415603318",
        answer: [{ valueString: exame.inputValue.code }],
      },
      exame?.category?.label && {
        linkId: "1383313118296",
        answer: [{ valueString: exame.category.label }],
      },
      // Adicionamos as entradas de resposta restantes se os valores não forem nulos
      exame?.condition?.label &&
        exame?.condition?.value && {
          linkId: "9571768447124",
          item: [
            {
              linkId: "8170939802572",
              answer: [{ valueString: exame.condition.label }],
            },
            {
              linkId: "4923176744451",
              answer: [{ valueString: exame.condition.value }],
            },
          ],
        },
      exame.note && {
        linkId: "3523489955010",
        answer: [{ valueString: exame.note }],
      },
    ].filter(Boolean); // Removemos valores nulos do array

    return {
      linkId: "5455138012969",
      item: answers,
    };
  });

  const conditions = objData?.condition?.length
    ? {
        linkId: "condition",
        answer: [
          objData?.condition?.map((el: any) => ({
            valueCoding: el,
          })),
        ],
      }
    : undefined;

  const certificatesAnswer = objData?.atestados?.map((atestado: any) => {
    const answers = [
      {
        linkId: "4095641279549",
        answer: [{ valueString: "Atestado comum" }],
      },
      atestado.note && {
        linkId: "3547687759041",
        answer: [{ valueString: atestado.note }],
      },
      atestado.note && {
        linkId: "8744868479001",
        answer: [{ valueString: atestado.note }],
      },
    ].filter(Boolean);
    return {
      linkId: "7695792866978",
      item: answers,
    };
  });

  const categoryAnswer = objData?.category
    ? {
        linkId: "category",
        answer: [
          {
            valueString: objData?.category.label,
          },
        ],
      }
    : undefined;

  if (!references)
    return [
      {
        linkId: "438586562091",
        answer: [{ valueString: objData?.nomeProtocolo }],
      },
      {
        linkId: "description",
        answer: [{ valueString: objData?.description || "" }],
      },
      {
        linkId: "referencias",
        answer: [{ valueString: objData?.referencias || "" }],
      },

      categoryAnswer,
      conditions,
      medicationAwnser,
      serviceAnswer,
      certificatesAnswer,
    ];
  else
    return JSON.stringify({
      resourceType: "QuestionnaireResponse",
      subject: {
        reference: `Practitioner/${oids.cpf}-${references.practitionerId}`,
      },

      status: objData?.status,
      questionnaire: `Questionnaire/${endpoints.PROTOCOL_ID}`,
      author: {
        reference: `Organization/${references.organizationId}`,
      },
      source: {
        reference: `Practitioner/${oids.cpf}-${references.practitionerId}`,
      },

      item: [
        {
          linkId: "438586562091",
          answer: [{ valueString: objData?.nomeProtocolo }],
        },
        {
          linkId: "description",
          answer: [{ valueString: objData?.description || "" }],
        },
        {
          linkId: "referencias",
          answer: [{ valueString: objData?.referencias || "" }],
        },
        categoryAnswer,
        conditions,
        medicationAwnser,
        serviceAnswer,
        certificatesAnswer,
      ],
    });
}

function Condition(objData: any, encounter: any) {
  const patientId = encounter.subject;
  return JSON.stringify({
    resourceType: "Condition",
    code: {
      coding: [
        {
          system: "http://www.saude.gov.br/fhir/r4/CodeSystem/BRCID10",
          code: objData?.condition?.code,
          display: objData?.condition?.display || objData?.condition?.value,
        },
      ],
    },
    participant: [
      {
        function: {
          coding: [{ code: "informant" }],
        },
        actor: {
          reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
        },
      },
      {
        function: {
          coding: [{ code: "enterer" }],
        },
        actor: {
          reference: `Practitioner/${oids.cpf}-${objData.practitionerId}`,
          display: objData?.practitionerName,
        },
      },
    ],
    clinicalStatus: {
      coding: [{ code: objData?.clinicalStatus?.value }],
    },
    verificationStatus: {
      coding: [{ code: objData?.verificationStatus?.value }],
    },
    ...(objData?.note ? { note: [{ text: objData.note }] } : {}),
    subject: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    encounter: {
      reference: `Encounter/${encounter.id}`,
    },
    recordedDate: dayjs().toISOString(),
  });
}

function CIAP2Condition(objData: any, encounter: any) {
  const patientId = encounter.subject;
  return JSON.stringify({
    resourceType: "Condition",
    meta: {
      profile: [
        "http://fluxmed.com.br/fhir/r5/StructureDefinition/FM-Condition",
      ],
    },
    clinicalStatus: {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/condition-clinical",
          code: "active",
        },
      ],
    },
    verificationStatus: {
      coding: [{ code: "confirmed" }],
    },

    category: [
      {
        coding: [
          {
            system: "http://terminology.hl7.org/CodeSystem/condition-category",
            code: "problem-list-item",
          },
        ],
      },
    ],
    code: {
      ...(objData?.code && {
        coding: [
          {
            system: "https://terminologia.saude.gov.br/fhir/CodeSystem/BRCIAP2",
            code: objData?.code,
            display: objData?.display,
          },
        ],
      }),
      text: objData?.text,
    },
    subject: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    encounter: {
      reference: `Encounter/${encounter.id}`,
    },
    participant: [
      // Enfermeiro
      {
        actor: {
          reference: `Practitioner/${oids.cpf}-${objData.practitionerId}`,
          display: objData?.practitionerName,
        },
      },
    ],
  });
}

function PhysicalFindingsObservation(objData: any, encounter: any) {
  const patientId = encounter.subject;

  return JSON.stringify({
    resourceType: "Observation",
    meta: {
      profile: [
        "http://fluxmed.com.br/fhir/r5/StructureDefinition/FM-Observation",
      ],
    },
    status: "final",
    code: {
      coding: [
        {
          ...(objData.code && {
            system: "http://loinc.org",
            code: objData?.code,
            display: objData?.display,
          }),
        },
      ],
      text: objData?.display || "Achados físicos",
    },
    subject: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    encounter: {
      reference: `Encounter/${encounter.id}`,
    },
    issued: dayjs().toISOString(), // Define data de emissão como a data/hora atual
    performer: [
      {
        reference: `Practitioner/${oids.cpf}-${objData.practitionerId}`,
        display: objData?.practitionerName,
      },
    ],
    interpretation: [
      {
        coding: [
          {
            system:
              "http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation",
            code: objData?.status === "normal" ? "N" : "A", // N = Normal, A = Alterado
            display: objData?.status === "normal" ? "Normal" : "Alterado",
          },
        ],
        text: objData?.description || "",
      },
    ],
    valueString: objData?.display,
  });
}

function ImmunizationNarrative(objData: any, encounter: any) {
  const patientId = encounter.subject;
  return JSON.stringify({
    resourceType: "Observation",
    meta: {
      profile: [
        "http://fluxmed.com.br/fhir/r5/StructureDefinition/FM-Observation",
      ],
    },
    status: "final",
    code: {
      coding: [
        {
          system: "http://loinc.org",
          code: "11369-6",
          display: "History of Immunization Narrative",
        },
      ],
      text: "Narrativa de vacinação",
    },
    subject: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    partOf: {
      reference: `Immunization/${objData?.immunizationId}`,
    },
    encounter: {
      reference: `Encounter/${encounter.id}`,
    },
    issued: dayjs(objData?.date).toISOString(),
    performer: [
      {
        reference: `Practitioner/${oids.cpf}-${objData.practitionerId}`,
        display: objData?.practitionerName,
      },
    ],
    valueString: objData?.narrative,
  });
}

function Immunization(objData: any, encounter: any) {
  const patientId = encounter.subject;
  return JSON.stringify({
    resourceType: "Immunization",
    meta: {
      profile: [
        "http://fluxmed.com.br/fhir/r5/StructureDefinition/FM-Immunization",
      ],
    },
    status: "completed",
    vaccineCode: {
      coding: [
        {
          system:
            "https://terminologia.saude.gov.br/fhir/CodeSystem/BRImunobiologico",
          code: objData?.vaccine?.code,
          display: objData?.vaccine?.display,
        },
      ],
    },
    patient: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    // um dos dois elementos "occurrence" apenas
    ...(objData?.date && {
      occurrenceDateTime: dayjs(objData?.date).toISOString(), // pode ser so o ano ou ano e mes ou data, p.e. 2028 ou 2028-06 ou 2028-06-04
    }),

    informationSource: {
      reference: {
        reference: `Practitioner/${oids.cpf}-${objData?.practitionerId}`,
        display: objData?.practitionerName,
      },
    },
    encounter: {
      reference: `Encounter/${encounter.id}`,
    },
  });
}

function ServiceRequest(objData: any, encounter: any, contained?: any) {
  // Prefer requisition hidratada (edição/lote misto); senão gera uuid novo para o lote.
  // Bundle transaction aqui é só POST — PUT individual fica em PutServiceRequest.
  const existingRequisition = objData?.find(
    (el: any) => el?.requisition?.value,
  )?.requisition;
  const uuidValue = existingRequisition?.value || `${uuid.v1()}`;
  const requisitionSystem =
    existingRequisition?.system || "urn:ietf:rfc:3986";
  const day = dayjs().toISOString();

  const patientId = encounter.subject;
  const services = objData?.map((el: any, index: number) => {
    const auxServices = {
      encounter: encounter.id,
      authoredOn: day,
      requester: encounter.requester,
      contained,
      subject: {
        reference: currentFeatures.defaultApp
          ? `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`
          : `Patient/${patientId}`,
      },
      condition: el?.condition,
      note: el?.note,
      categoryCode: el?.category?.code,
      categoryDisplay: el?.category?.display,
      concept: {
        coding: [
          {
            system:
              "https://fluxmed.com.br/fhir/r5/CodeSystem/TUSSProcedimentosEventos",
            code: el?.inputValue?.code,
            display: el?.name,
          },
        ],
      },
      medicatonRef: el?.medication,
      procedure: el.type === "procedure" ? true : false,
    };

    return {
      resource: {
        authoredOn: auxServices?.authoredOn,
        resourceType: "ServiceRequest",
        status: "active",
        // Reusa identifier FHIR na edição; senão gera a partir do uuid do lote
        identifier: el?.identifier?.length
          ? el.identifier
          : [
              {
                value: `${uuidValue}.${index + 1}`,
                system: "urn:ietf:rfc:3986",
              },
            ],
        contained: contained,
        requisition: {
          value: `${uuidValue}`,
          system: requisitionSystem,
        },
        reason: {
          concept: {
            coding: [auxServices?.condition],
          },
        },
        requester: auxServices.requester,
        subject: auxServices.subject,
        encounter: {
          reference: `Encounter/${auxServices.encounter}`,
        },
        code: {
          concept: auxServices.concept,
        },
        ...(auxServices.note && {
          note: { text: auxServices.note },
        }),
        category: [
          auxServices.categoryDisplay
            ? {
                coding: [
                  {
                    ...(auxServices.categoryCode && {
                      system: "http://snomed.info/sct",
                      code: auxServices.categoryCode,
                    }),
                    display: auxServices.categoryDisplay,
                  },
                ],
              }
            : null,
          auxServices.procedure === true && { text: "procedure" },
        ].filter(Boolean),
        intent: "order",
      },
      request: {
        url: "ServiceRequest",
        method: "POST",
      },
    };
  });

  return JSON.stringify({
    resourceType: "Bundle",
    type: "transaction",
    meta: {
      profile: [
        "http://fluxmed.com.br/fhir/StructureDefinition/ServiceRequestBundle-1.0",
      ],
    },
    entry: services,
  });
}

function MedicationRequest(objData: any, encounter?: any, source?: any) {
  // Prefer groupIdentifier hidratado (edição/lote misto); senão gera uuid novo para o lote.
  // Bundle transaction aqui é só POST — PUT individual fica em PutMedicationRequest.
  const existingGroup = objData?.find(
    (el: any) => el?.groupIdentifier?.value,
  )?.groupIdentifier;
  const uuidValue = existingGroup?.value || `${uuid.v1()}`;
  const groupSystem = existingGroup?.system || "urn:ietf:rfc:3986";
  const day = dayjs().toISOString();

  function extrairNumero(string: string) {
    const match = string?.match(/\d+/);
    return match ? Number(match[0]) : null;
  }

  function removerNumerosIniciais(string: string) {
    return string?.replace(/^\d+\s*/, "");
  }

  const patientId = encounter?.subject || source?.subject;

  const medications = objData?.map((el: any, index: number) => {
    const auxMedication = {
      encounter: encounter?.id,
      requester: encounter?.requester,
      subject: {
        reference: currentFeatures.defaultApp
          ? `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`
          : `Patient/${patientId}`,
      },

      note: el.note,
      period: el.time,
      frequency: el.frequency,
      quantity: el.quantity,
      dosage: !!(el.frequency || el.period),
      form: el.formValue,
      concept: {
        ...(el.code && el.code),
        text: el.name,
      },
      medicatonRef: el.medication,
    };
    return {
      resource: {
        resourceType: "MedicationRequest",
        status: source?.subject ? "active" : "draft",
        // Reusa identifier FHIR na edição; senão gera a partir do uuid do lote
        identifier: el?.identifier?.length
          ? el.identifier
          : [
              {
                value: `${uuidValue}.${index + 1}`,
                system: "urn:ietf:rfc:3986",
              },
            ],
        groupIdentifier: {
          value: `${uuidValue}`,
          system: groupSystem,
        },
        ...(auxMedication.requester &&
          !source?.subject && {
            requester: auxMedication.requester,
          }),

        informationSource: [
          {
            ...(!auxMedication.requester &&
              source?.subject && {
                reference: `Patient/${oids.cpf}-${patientId}`,
              }),
          },
          {
            reference: auxMedication?.requester?.reference,
            display: auxMedication?.requester?.display,
          },
        ],

        subject: auxMedication.subject,
        ...(auxMedication.encounter && {
          encounter: {
            reference: `Encounter/${auxMedication.encounter}`,
          },
        }),

        medication: {
          ...(auxMedication.concept && {
            concept: auxMedication.concept,
          }),
        },
        ...(auxMedication.note && {
          note: {
            authorReference: {
              // Se for visão profissional (não tem source.subject), usa o requester; se for visão do paciente, usa o Patient
              reference: !source?.subject
                ? auxMedication.requester?.reference
                : `Patient/${oids.cpf}-${patientId}`,
            },
            text: auxMedication.note,
          },
        }),
        ...(day && {
          effectiveDosePeriod: {
            start: day,
            ...(auxMedication?.period?.value && {
              end: dayjs(day)
                ?.add(auxMedication?.period?.value, "days")
                ?.toISOString(),
            }),
          },
        }),
        ...(auxMedication.dosage && {
          dosageInstruction: [
            {
              timing: {
                repeat: {
                  // count: 2,
                  periodUnit: "h",
                  frequency: 1,
                  // timeOfDay: ["08:00:00"],
                  // dayOfWeek: ["mon"],
                  period: auxMedication?.frequency?.value,
                  duration: auxMedication?.period?.value, // Duração total do tratamento
                  durationUnit: "d",
                },
              },
              doseAndRate: [
                {
                  type: {
                    coding: [
                      {
                        system:
                          "http://www.fluxmed.com.br/codesystem/dose-type",
                        code: auxMedication.form?.value,
                        display: auxMedication.form?.label,
                      },
                    ],
                    text: auxMedication.form?.label,
                  },
                  ...(auxMedication?.quantity && {
                    doseQuantity: {
                      value: extrairNumero(
                        auxMedication.quantity?.label || auxMedication.quantity,
                      ),
                      unit:
                        removerNumerosIniciais(
                          auxMedication.quantity?.label ||
                            auxMedication.quantity,
                        ) || auxMedication.form?.label,
                    },
                  }),
                },
              ],
            },
          ],
        }),
        intent: "order",
      },
      request: {
        url: "MedicationRequest",
        method: "POST",
      },
    };
  });

  return JSON.stringify({
    resourceType: "Bundle",
    type: "transaction",
    meta: {
      profile: [
        "http://fluxmed.com.br/fhir/StructureDefinition/MedicationRequestBundle-1.0",
      ],
    },
    entry: medications,
  });
}

function MedicationStatement(
  objData: any,
  encounter?: any,
  patientIdentifier?: any,
) {
  function extrairNumero(string: string) {
    const match = string?.match(/\d+/);
    return match ? Number(match[0]) : null;
  }

  function removerNumerosIniciais(string: string) {
    return string?.replace(/^\d+\s*/, "");
  }

  const patientId = encounter ? encounter.subject : patientIdentifier;

  const auxMedication = {
    ...(encounter && { encounter: encounter.id }),
    subject: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    ...(objData?.time && { period: objData?.time }),
    frequency: objData?.frequency,
    quantity: objData?.quantity,
    ...((objData?.frequency || objData?.period) && {
      dosage: !!(objData?.frequency || objData?.period),
    }),
    ...(objData?.form && { form: objData?.form }),
    concept: {
      ...(objData?.medication?.code && objData?.medication?.code),
      text: objData?.medication?.name || objData.medication,
    },
    ...(objData.note && { note: objData?.note }),
  };

  const medicationStatement = {
    resourceType: "MedicationStatement",
    status: "recorded",
    dateAsserted: objData?.dateAssert || dayjs().toISOString(),
    subject: auxMedication?.subject,
    ...(auxMedication?.encounter && {
      encounter: {
        reference: `Encounter/${auxMedication?.encounter}`,
      },
    }),
    informationSource: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    medication: {
      ...(auxMedication?.concept && {
        concept: auxMedication.concept,
      }),
    },
    ...(auxMedication.dosage && {
      dosage: [
        {
          timing: {
            repeat: {
              // count: 2,
              periodUnit: "h",
              frequency: 1,
              // timeOfDay: ["08:00:00"],
              // dayOfWeek: ["mon"],
              period: auxMedication?.frequency?.value,
              duration: auxMedication?.period?.value, // Duração total do tratamento
              durationUnit: "d",
            },
          },
          doseAndRate: [
            {
              type: {
                coding: [
                  {
                    system: "http://www.fluxmed.com.br/codesystem/dose-type",
                    code: auxMedication.form?.value,
                    display: auxMedication.form?.label,
                  },
                ],
                text: auxMedication.form?.label,
              },
              ...(auxMedication?.quantity && {
                doseQuantity: {
                  value: extrairNumero(
                    auxMedication.quantity?.label || auxMedication.quantity,
                  ),
                  unit:
                    removerNumerosIniciais(
                      auxMedication.quantity?.label || auxMedication.quantity,
                    ) || auxMedication.form?.label,
                },
              }),
            },
          ],
        },
      ],
    }),
    ...(auxMedication.note && {
      note: [
        {
          text: auxMedication.note,
        },
      ],
    }),
  };

  return JSON.stringify(medicationStatement);
}

function DocumentReferenceJson(objData: any, encounter: any, refs: any) {
  const uuidValue = `${uuid.v1()}`;
  const day = dayjs().toISOString();

  const patientId = encounter.subject;
  const practitionerId = objData?.practitionerId;
  const practitionerRole = encounter?.requester?.reference;
  const organizationId = objData?.organizationId;

  let contained = [];

  if (practitionerId && organizationId) {
    contained = [
      {
        resourceType: "Practitioner",
        id: "practitioner1",
        identifier: [
          {
            system: `urn:oid:${oids.cpf}`,
            value: practitionerId,
          },
        ],
        name: [{ text: objData?.practitioner?.name }],
      },
      {
        resourceType: "Organization",
        id: "organization1",
        identifier: [
          {
            system: "urn:oid:${oids.cnpj}",
            value: organizationId,
          },
        ],
        name: objData?.organization?.name,
        alias: [objData?.organization?.alias],
      },
    ];
  } else {
    contained = [
      {
        resourceType: "Patient",
        id: "patient1",
        identifier: [
          {
            system: `urn:oid:${oids.cpf}`,
            value: patientId,
          },
        ],
      },
    ];
  }

  let author = [];
  if (practitionerId && organizationId && practitionerRole) {
    author = [
      {
        reference: "#practitioner1",
      },
      {
        reference: `Practitioner/${oids.cpf}-${practitionerId}`,
      },
      {
        reference: practitionerRole,
      },
      {
        reference: "#organization1",
      },
      {
        reference: `Organization/${oids.cnpj}-${organizationId}`,
      },
    ];
  } else {
    author = [
      {
        reference: "#patient1",
      },
      {
        reference: `Patient/${oids.cpf}-${patientId}`,
      },
    ];
  }

  return JSON.stringify({
    meta: {
      profile: [
        "http://ihe.net/fhir/StructureDefinition/IHE_MHD_Provide_Comprehensive_DocumentBundle",
      ],
    },
    resourceType: "Bundle",
    type: "transaction",
    entry: [
      {
        resource: {
          resourceType: "DocumentReference",
          id: uuidValue,
          contained: contained,
          identifier: [
            {
              system: "urn:ietf:rfc:3986",
              value: `urn:oid:1.3.6.1.4.1.54413.3.2.3.${patientId}.10.${uuidValue}.2`,
            },
            {
              use: "official",
              system: "urn:ietf:rfc:3986",
              value: `urn:uuid:${uuidValue}`,
            },
          ],
          basedOn: refs,
          status: "current",
          type: {
            coding: [
              {
                system: "urn:oid:1.3.6.1.4.1.54413.3.3.6.16",
                code: objData.codeValue,
                display: objData.codeDisplay,
              },
            ],
          },
          category: [
            {
              coding: [
                {
                  system: "urn:oid:1.3.6.1.4.1.54413.3.3.6.16",
                  code: objData.categoryValue,
                  display: objData.categoryDisplay,
                },
              ],
            },
          ],
          subject: {
            reference: `Patient/${oids.cpf}-${patientId}`,
          },
          context: [
            {
              reference: `Encounter/${encounter?.id}`,
            },
          ],
          practiceSetting: {
            coding: [
              {
                system: objData?.servicePratice?.system,
                code: objData?.servicePratice?.code,
                display: objData?.servicePratice?.display,
              },
            ],
          },
          period: {
            start: objData?.time?.start,
            end: objData?.time?.end,
          },
          date: day,
          author: author,
          securityLabel: [
            {
              coding: [
                {
                  system: "urn:oid:2.16.840.1.113883.5.25",
                  code: "N",
                  display: "Normal",
                },
              ],
            },
          ],
          content: [
            {
              attachment: {
                contentType: "application/pdf",
                url: `Binary/1.3.6.1.4.1.54413.3.2.3.${patientId}.10.${uuidValue}.2`,
                creation: day,
                language: "pt-BR",
              },
              profile: [
                {
                  valueCoding: {
                    system: "urn:oid:1.3.6.1.4.1.54413.3.3.6.16",
                    code: "application/pdf",
                  },
                },
              ],
            },
          ],
        },
        request: {
          method: "PUT",
          url: `DocumentReference/${uuidValue}`,
        },
      },
      {
        resource: {
          resourceType: "Binary",
          id: `1.3.6.1.4.1.54413.3.2.3.${patientId}.10.${uuidValue}.2`,
          contentType: "application/pdf",
          data: objData.base64,
        },
        request: {
          method: "PUT",
          url: `Binary/1.3.6.1.4.1.54413.3.2.3.${patientId}.10.${uuidValue}.2`,
        },
      },
    ],
  });
}

/**
 * Bundle MHD simplificado: DocumentReference + Binary.
 * Author/subject = paciente; sem Encounter, Practitioner ou Organization.
 * Uuid é gerado só aqui — o caller não deve passar uuid.
 * type reutiliza categoryValue/categoryDisplay para não ampliar a assinatura.
 * contentType é recebido porque o base64 pode ser PDF, imagem, etc.
 * relatesTo (opcional) = substituição: query "identifier=<system>%7C<value>"
 * do documento anterior, igual ao padrão XML legado (code "replaces").
 * description (opcional) = identificação legível informada pelo paciente.
 */
function DocumentReferencePatientJson(
  base64: string,
  patientId: string,
  categoryValue: string,
  categoryDisplay: string,
  contentType: string,
  relatesTo?: string,
  description?: string,
) {
  const uuidValue = `${uuid.v1()}`;
  const day = dayjs().toISOString();
  // OID do Binary alinhado ao padrão legado (patientId + uuid)
  const binaryId = `1.3.6.1.4.1.54413.3.2.3.${patientId}.10.${uuidValue}.2`;
  const patientReference = `Patient/${oids.cpf}-${patientId}`;

  // Coding compartilhado entre type e category (mesmos valores da categoria)
  const categoryCoding = {
    system: "urn:oid:1.3.6.1.4.1.54413.3.3.6.16",
    code: categoryValue,
    display: categoryDisplay,
  };

  return JSON.stringify({
    meta: {
      profile: [
        "http://ihe.net/fhir/StructureDefinition/IHE_MHD_Provide_Comprehensive_DocumentBundle",
      ],
    },
    resourceType: "Bundle",
    type: "transaction",
    entry: [
      {
        resource: {
          resourceType: "DocumentReference",
          id: uuidValue,
          // Contained mínimo: só Patient (author = paciente)
          contained: [
            {
              resourceType: "Patient",
              id: "patient1",
              identifier: [
                {
                  system: `urn:oid:${oids.cpf}`,
                  value: patientId,
                },
              ],
            },
          ],
          identifier: [
            {
              system: "urn:ietf:rfc:3986",
              value: `urn:oid:${binaryId}`,
            },
            {
              use: "official",
              system: "urn:ietf:rfc:3986",
              value: `urn:uuid:${uuidValue}`,
            },
          ],
          status: "current",
          ...(description?.trim()
            ? { description: description.trim() }
            : {}),
          type: {
            coding: [categoryCoding],
          },
          category: [
            {
              coding: [categoryCoding],
            },
          ],
          subject: {
            reference: patientReference,
          },
          // Sem context (Encounter) de propósito neste fluxo simplificado
          date: day,
          // relatesTo replaces: aponta para o doc anterior via busca por identifier
          // (mesmo shape do legado XML em DocumentReference/auxRelatesTo)
          ...(relatesTo
            ? {
                relatesTo: [
                  {
                    code: "replaces",
                    target: {
                      reference: `DocumentReference?${relatesTo}`,
                    },
                  },
                ],
              }
            : {}),
          author: [
            { reference: "#patient1" },
            { reference: patientReference },
          ],
          securityLabel: [
            {
              coding: [
                {
                  system: "urn:oid:2.16.840.1.113883.5.25",
                  code: "N",
                  display: "Normal",
                },
              ],
            },
          ],
          content: [
            {
              attachment: {
                contentType: contentType,
                url: `Binary/${binaryId}`,
                creation: day,
                language: "pt-BR",
              },
              profile: [
                {
                  valueCoding: {
                    system: "urn:oid:1.3.6.1.4.1.54413.3.3.6.16",
                    code: contentType,
                  },
                },
              ],
            },
          ],
        },
        request: {
          method: "PUT",
          url: `DocumentReference/${uuidValue}`,
        },
      },
      {
        resource: {
          resourceType: "Binary",
          id: binaryId,
          contentType: contentType,
          data: base64,
        },
        request: {
          method: "PUT",
          url: `Binary/${binaryId}`,
        },
      },
    ],
  });
}

function TissDocumentReference(objData: any, tissXML: any) {
  const uuidValue = `${uuid.v1()}`;
  const day = dayjs().toISOString();
  const guideBase64 = base64.encode(tissXML);
  const cnpj = objData?.organizationId;
  const organizationName = objData?.organizationName;
  const organizationAlias = objData?.organization?.alias;
  const practitionerId = objData?.practitionerId;
  const practitionerName = objData?.practitionerName;
  const patientId = objData?.patientId;

  const bundleXML = `<fhir:Bundle xmlns:fhir="http://hl7.org/fhir">
  <fhir:meta>
    <fhir:profile value="http://ihe.net/fhir/StructureDefinition/IHE_MHD_Provide_Comprehensive_DocumentBundle"/>
  </fhir:meta>
  <fhir:type value="transaction"/>
  <fhir:entry>
    <fhir:resource>
      <fhir:DocumentReference>
        <fhir:id value="${uuidValue}"/>

        <fhir:meta>
          <fhir:tag>
            <fhir:system value="urn:oid:${oids.cnpj}"/>
            <fhir:code value="${cnpj}"/>
          </fhir:tag>
        </fhir:meta>

        <fhir:contained>
          <fhir:Practitioner>
            <fhir:id value="practitioner1"/>
            <fhir:identifier>
              <fhir:system value="urn:oid:${oids.cpf}"/>
              <fhir:value value="${practitionerId}"/>
            </fhir:identifier>
            <fhir:name>
              <fhir:given value="${practitionerName}"/>
            </fhir:name>
          </fhir:Practitioner>
        </fhir:contained>

        <fhir:contained>
          <fhir:Organization>
            <fhir:id value="organization1"/>
            <fhir:identifier>
              <fhir:system value="urn:oid:${oids.cnpj}"/>
              <fhir:value value="${cnpj}"/>
            </fhir:identifier>
            <fhir:name value="${organizationName}"/>
            <fhir:alias value="${organizationAlias}"/>
          </fhir:Organization>
        </fhir:contained>

        <fhir:contained>
          <fhir:Patient>
            <fhir:id value="patient1"/>
            <fhir:identifier>
              <fhir:system value="urn:oid:${oids.cpf}"/>
              <fhir:value value="${patientId}"/>
            </fhir:identifier>
          </fhir:Patient>
        </fhir:contained>

        <fhir:identifier>
          <fhir:system value="urn:ietf:rfc:3986"/>
          <fhir:value value="urn:oid:1.3.6.1.4.1.54413.3.2.3.${cnpj}.20.${uuidValue}.1"/>
        </fhir:identifier>
        <fhir:identifier>
          <fhir:use value="official"/>
          <fhir:system value="urn:ietf:rfc:3986"/>
          <fhir:value value="urn:uuid:${uuidValue}"/>
        </fhir:identifier>

        <fhir:status value="current"/>

        <fhir:context>
          <fhir:reference value="Appointment/${objData?.appointmentId}"/>
        </fhir:context>

        <fhir:type>
          <fhir:coding>
            <fhir:system value="1.3.6.1.4.1.54413.3.3.6.16"/>
            <fhir:code value="tiss"/>
            <fhir:display value="Guia TISS"/>
          </fhir:coding>
        </fhir:type>

        <fhir:category>
          <fhir:coding>
            <fhir:system value="1.3.6.1.4.1.54413.3.3.6.16"/>
            <fhir:code value="guias"/>
            <fhir:display value="Guias de Atendimento"/>
          </fhir:coding>
        </fhir:category>

        <fhir:subject>
          <fhir:reference value="Patient/${oids.cpf}-${patientId}"/>
        </fhir:subject>

        <fhir:date value="${day}"/>

        <fhir:author>
          <fhir:reference value="#practitioner1"/>
        </fhir:author>
        <fhir:author>
          <fhir:reference value="#organization1"/>
        </fhir:author>
        <fhir:author>
          <fhir:reference value="Organization/${oids.cnpj}-${cnpj}"/>
        </fhir:author>

        <fhir:securityLabel>
          <fhir:coding>
            <fhir:system value="2.16.840.1.113883.5.25"/>
            <fhir:code value="N"/>
            <fhir:display value="Normal"/>
          </fhir:coding>
        </fhir:securityLabel>

        <fhir:content>
          <fhir:attachment>
            <fhir:contentType value="application/xml"/>
            <fhir:language value="pt-BR"/>
            <fhir:url value="Binary/1.3.6.1.4.1.54413.3.2.3.${cnpj}.20.${uuidValue}.1"/>
   
            <fhir:creation value="${day}"/>
          </fhir:attachment>
          <fhir:profile>
            <fhir:valueCoding>
              <fhir:system value="1.3.6.1.4.1.54413.3.3.6.16"/>
              <fhir:code value="application/xml"/>
            </fhir:valueCoding>
          </fhir:profile>
        </fhir:content>
      </fhir:DocumentReference>
    </fhir:resource>
    <fhir:request>
      <fhir:method value="PUT"/>
      <fhir:url value="DocumentReference/${uuidValue}"/>
    </fhir:request>
  </fhir:entry>

  <fhir:entry>
    <fhir:resource>
      <fhir:Binary>
        <fhir:id value="1.3.6.1.4.1.54413.3.2.3.${cnpj}.20.${uuidValue}.1"/>
        <fhir:contentType value="application/xml"/>
        <fhir:data value="${guideBase64}"/>
      </fhir:Binary>
    </fhir:resource>
    <fhir:request>
      <fhir:method value="PUT"/>
      <fhir:url value="Binary/1.3.6.1.4.1.54413.3.2.3.${cnpj}.20.${uuidValue}.1"/>
    </fhir:request>
  </fhir:entry>
</fhir:Bundle>
`;

  return bundleXML;
}

function DocumentReference(
  objData: any,
  encounter: any,
  refs: any,
  signed?: boolean,
  uuidDefault?: any,
  idValue?: any,
) {
  const uuidValue = `${uuidDefault || uuid.v1()}`;
  const day = dayjs().toISOString();

  const patientId = encounter.subject;
  const practitionerId = objData?.practitioner?.id?.split("-")?.[1];
  const practitionerRole = encounter?.requester?.reference;
  const organizationId = objData?.organization?.id?.split("-")?.[1];

  const auxTag = organizationId
    ? ` <fhir:meta>
  <fhir:tag>
    <fhir:system value="urn:oid:${oids.cnpj}"/>
    <fhir:code value="${organizationId}"/>
  </fhir:tag>
</fhir:meta>`
    : " ";

  const auxContained =
    practitionerId && organizationId
      ? `<fhir:contained>
<fhir:Practitioner>
  <fhir:id value="practitioner1"/>
  <fhir:identifier>
    <fhir:system value="urn:oid:${oids.cpf}"/>
    <fhir:value value="${practitionerId}"/>
  </fhir:identifier>
  <fhir:name>
    <fhir:given value="${objData?.practitioner?.name}"/>
  </fhir:name>
</fhir:Practitioner>
</fhir:contained>
<fhir:contained>
    <fhir:Organization>
      <fhir:id value="organization1"/>
      <fhir:identifier>
        <fhir:system value="urn:oid:${oids.cnpj}"/>
        <fhir:value value="${organizationId}"/>
      </fhir:identifier>
      <fhir:name value="${objData?.organization?.name}"/>
      <fhir:alias value="${objData?.organization?.alias}"/>
    </fhir:Organization>
  </fhir:contained>
`
      : ` <fhir:contained>
    <fhir:Patient>
      <fhir:id value="patient1"/>
      <fhir:identifier>
        <fhir:system value="urn:oid:${oids.cpf}"/>
        <fhir:value value="${patientId}"/>
      </fhir:identifier>
    </fhir:Patient>
  </fhir:contained>`;

  const auxAuthor =
    practitionerId && organizationId && practitionerRole
      ? ` <fhir:author>
    <fhir:reference value="#practitioner1"/>
  </fhir:author>
  <fhir:author>
    <fhir:reference value="Practitioner/${oids.cpf}-${practitionerId}"/>
  </fhir:author>
  <fhir:author>
    <fhir:reference value="${practitionerRole}"/>
  </fhir:author>
  <fhir:author>
    <fhir:reference value="#organization1"/>
  </fhir:author>`
      : ` <fhir:author>
  <fhir:reference value="#patient"/>
</fhir:author>
<fhir:author>
  <fhir:reference value="Patient/${
    currentFeatures.defaultApp ? `${oids.cpf}-${patientId}` : `${patientId}`
  }"/>
</fhir:author>`;

  const auxRelatesTo = objData?.relatesTo
    ? `<fhir:relatesTo>
      <fhir:code value="replaces"/>
      <fhir:target>
         <fhir:reference value="DocumentReference?${objData?.relatesTo}"/>
      </fhir:target>
</fhir:relatesTo>`
    : " ";

  const securityLabel = signed
    ? `
<fhir:securityLabel>
 <fhir:coding>
   <fhir:system value="http://hl7.org/fhir/ValueSet/signature-type"/>
  <fhir:code  value="ProofOfOrigin"/>
  <fhir:display value="Proof of origin"/>
</fhir:coding>
</fhir:securityLabel>`
    : `<fhir:securityLabel>
        <fhir:coding>
          <fhir:system value="2.16.840.1.113883.5.25"/>
          <fhir:code value="N"/>
          <fhir:display value="Normal"/>
        </fhir:coding>
      </fhir:securityLabel>`;

  const documentReference = `<fhir:entry>
  <fhir:resource>
    <fhir:DocumentReference>
      <fhir:id value="${uuidValue}"/>
      ${auxTag}
      ${auxContained}
      <fhir:identifier>
        <fhir:system value="urn:ietf:rfc:3986"/>
        <fhir:value value="urn:oid:1.3.6.1.4.1.54413.3.2.3.${
          organizationId || patientId
        }.10.${idValue}.2"/>
      </fhir:identifier>
      <fhir:identifier>
        <fhir:use value="official"/>
        <fhir:system value="urn:ietf:rfc:3986"/>
        <fhir:value value="urn:uuid:${uuidValue}"/>
      </fhir:identifier>
       ${refs || " "}
      <fhir:status value="current"/>
      <fhir:type>
        <fhir:coding>
          <fhir:system value="1.3.6.1.4.1.54413.3.3.6.16"/>
          <fhir:code value="${objData.codeValue}"/>
          <fhir:display value="${objData.codeDisplay}"/>
        </fhir:coding>
      </fhir:type>
      <fhir:category>
        <fhir:coding>
          <fhir:system value="1.3.6.1.4.1.54413.3.3.6.16"/>
          <fhir:code value="${objData.categoryValue}"/>
          <fhir:display value="${objData.categoryDisplay}"/>
        </fhir:coding>
      </fhir:category>
      <fhir:subject>
        <fhir:reference value="Patient/${
          currentFeatures.defaultApp
            ? `${oids.cpf}-${patientId}`
            : `${patientId}`
        }" />
      </fhir:subject>
      <fhir:context>
        <fhir:reference value="Appointment/${encounter.appointmentId}"/>
      </fhir:context>
      <fhir:context>
        <fhir:reference value="Encounter/${encounter?.id}"/>
      </fhir:context>
      <fhir:practiceSetting>
        <fhir:coding>
          <fhir:system value="${objData?.servicePratice?.system}"/>
          <fhir:code value="${objData?.servicePratice?.code}"/>
          <fhir:display value="${objData?.servicePratice?.display}"/>
        </fhir:coding>
      </fhir:practiceSetting>
      <fhir:period>
        <fhir:start value="${objData?.time?.start}"/>
        <fhir:end value="${objData?.time?.end}"/>
      </fhir:period>
      <fhir:date value="${day}"/>
     ${auxAuthor}
     
     ${auxRelatesTo}

     ${securityLabel}
      
      <fhir:content>
        <fhir:attachment>
          <fhir:contentType value="application/pdf"/>
          <fhir:language value="pt-BR"/>
          <fhir:url value="Binary/1.3.6.1.4.1.54413.3.2.3.${
            organizationId || patientId
          }.10.${idValue}.2"/>
          <fhir:creation value="${day}"/>
        </fhir:attachment>
        <fhir:profile>
          <fhir:valueCoding>
            <fhir:system value="1.3.6.1.4.1.54413.3.3.6.16"/>
            <fhir:code value="application/pdf"/>
          </fhir:valueCoding>
        </fhir:profile>
      </fhir:content>
    </fhir:DocumentReference>
  </fhir:resource>
  <fhir:request>
    <fhir:method value="PUT"/>
    <fhir:url value="DocumentReference/${uuidValue}"/>
  </fhir:request>
</fhir:entry>`;

  const binary = `<fhir:entry>
<fhir:resource>
  <fhir:Binary>
    <fhir:id value="1.3.6.1.4.1.54413.3.2.3.${
      organizationId || patientId
    }.10.${idValue}.2"/>
    <fhir:contentType value="application/pdf"/>
    <fhir:data value="${objData.base64}"/>
  </fhir:Binary>
</fhir:resource>
<fhir:request>
  <fhir:method value="PUT"/>
  <fhir:url value="Binary/1.3.6.1.4.1.54413.3.2.3.${
    organizationId || patientId
  }.10.${idValue}.2"/>
</fhir:request>
</fhir:entry>`;

  const bundleXML = `<fhir:Bundle
	xmlns:fhir="http://hl7.org/fhir">
	<fhir:meta>
		<fhir:profile value="http://ihe.net/fhir/StructureDefinition/IHE_MHD_Provide_Comprehensive_DocumentBundle"/>
	</fhir:meta>
	<fhir:type value="transaction"/>
	${documentReference}
  ${binary}

</fhir:Bundle>`;

  return bundleXML;
}

function PatientSummary(identifier: string, entries?: string[]) {
  const numberOfDocsToRequest = 100;
  // Extrai o ID completo do paciente no formato oids.cpf-identifier

  const patientId = currentFeatures.defaultApp
    ? `${oids.cpf}-${identifier}`
    : identifier;
  const bundleEntries = [
    // {
    //   request: {
    //     method: "GET",
    //     url: `Observation?code=http://loinc.org|55284-4&subject=Patient/${oids.cpf}-${identifier}&status=final&_sort=-date&_count=1`,
    //   },
    // },
    // {
    //   request: {
    //     method: "GET",
    //     url: `Observation?code=http://loinc.org|29463-7&subject=Patient/${oids.cpf}-${identifier}&status=final&_sort=-date&_count=1`,
    //   },
    // },
    // {
    //   request: {
    //     method: "GET",
    //     url: `Observation?code=http://loinc.org|8302-2&subject=Patient/${oids.cpf}-${identifier}&status=final&_sort=-date&_count=1`,
    //   },
    // },
    {
      request: {
        method: "GET",
        url: `Observation?subject=Patient/${patientId}&status=final&_sort=-_lastUpdated&_count=${350}`,
      },
    },
    {
      request: {
        method: "GET",
        url: `Condition?subject=Patient/${patientId}&verification-status=unconfirmed,provisional,confirmed,refuted&_sort=-recorded-date&_count=${numberOfDocsToRequest}`,
      },
    },
    {
      request: {
        method: "GET",
        url: `Immunization?patient=Patient/${patientId}&status=completed&_sort=-_lastUpdated&_count=${numberOfDocsToRequest}`,
      },
    },
    {
      request: {
        method: "GET",
        url: `Encounter?subject=Patient/${patientId}&_sort=-date&_count=${numberOfDocsToRequest}`,
      },
    },
    // {
    //   request: {
    //     method: "GET",
    //     url: `Condition?code=2.16.840.1.113883.6.139|,http://www.saude.gov.br/fhir/r4/CodeSystem/BRCIAP2|&subject=Patient/${oids.cpf}-${identifier}&_sort=-recorded-date&_count=${numberOfDocsToRequest}`,
    //   },
    // },
    {
      request: {
        method: "GET",
        url: `AllergyIntolerance?patient=Patient/${patientId}&verification-status=unconfirmed,presumed,confirmed,refuted&_sort=-date&_count=${numberOfDocsToRequest}`,
      },
    },
    // {
    //   request: {
    //     method: "GET",
    //     url: `Procedure?patient=Patient/${oids.cpf}-${identifier}&status=completed&_sort=-date&_count=${numberOfDocsToRequest}`,
    //   },
    // },
    {
      request: {
        method: "GET",
        url: `MedicationRequest?subject=Patient/${patientId}&_sort=-_lastUpdated&_count=${numberOfDocsToRequest}&status=active,on-hold,ended,stopped,completed,cancelled,draft,unknown`,
      },
    },
    {
      request: {
        method: "GET",
        url: `CarePlan?subject=Patient/${patientId}&status=active&_count=${numberOfDocsToRequest}&_sort=-_lastUpdated`,
      },
    },
    // {
    //   request: {
    //     method: "GET",
    //     url: `MedicationStatement?subject=Patient/${oids.cpf}-${identifier}&_count=${numberOfDocsToRequest}&_sort=-_lastUpdated`,
    //   },
    // },
    // {
    //   request: {
    //     method: "GET",
    //     url: `DocumentReference?subject=Patient/${oids.cpf}-${identifier}&type:coding=Sum%C3%A1rioAlta.v4.0,Sum%C3%A1rio%20de%20Alta%20v1.0&_sort=-period&_count=${numberOfDocsToRequest}`,
    //   },
    // },
    {
      request: {
        method: "GET",
        url: `ServiceRequest?subject=Patient/${patientId}&status=active,completed&_sort=-authored&_count=${numberOfDocsToRequest}`,
      },
    },
    // Sem status=current em atestado/prescricao/service-request para permitir
    // montar cadeia de remissões (relatesTo/replaces) no sumário e na agenda.
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&category=atestado&_count=100&_sort=-_lastUpdated&_include=DocumentReference:based-on`,
      },
    },
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&category=prescricao&_count=100&_sort=-_lastUpdated&_include=DocumentReference:based-on`,
      },
    },
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&category=service-request&_count=100&_sort=-_lastUpdated&_include=DocumentReference:based-on`,
      },
    },
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&status=current&category=resultado&_count=100&_sort=-_lastUpdated`,
      },
    },
    // Documentos de suporte canabinoide (category = typeId do POST DocumentReferencePatientJson)
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&status=current&category=anvisa_import_authorization&_count=100&_sort=-_lastUpdated`,
      },
    },
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&status=current&category=medical_prescription&_count=100&_sort=-_lastUpdated`,
      },
    },
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&status=current&category=personal_document&_count=100&_sort=-_lastUpdated`,
      },
    },
    {
      request: {
        method: "GET",
        url: `/DocumentReference?subject=Patient/${patientId}&status=current&category=proof_of_residence&_count=100&_sort=-_lastUpdated`,
      },
    },
    {
      request: {
        method: "GET",
        url: `/QuestionnaireResponse?subject=Patient/${patientId}&_count=100&_sort=-_lastUpdated`,
      },
    },
  ];

  const filterEntries = bundleEntries?.filter((e: any) => {
    const verifis = entries?.some((ele: any) => e?.request?.url?.includes(ele));
    return verifis;
  });

  return JSON.stringify({
    resourceType: "Bundle",
    meta: {
      profile: ["http://ehrrunner.com/fhir/StructureDefinition/PatientSummary"],
    },
    entry: entries?.length ? filterEntries : bundleEntries,
  });
}

function Email(objData: IEmail) {
  return {
    cpf: objData.identifier,
    email: objData.email,
  };
}

function Patch(objData: IPatch) {
  return objData.operations?.map(({ op, path, value }) => ({
    op: op,
    path: path,
    value: value,
  }));
}

/**
 * Cria um objeto Task no formato FHIR R5
 * @param objData - Dados da tarefa a ser criada
 * @returns JSON string representando o recurso Task
 */
function ServiceRequestNoteOnly(
  noteText: string,
  encounter: any,
  basedOn?: Array<{ reference: string }>,
  uuidValues?: string,
) {
  const uuidValue = uuidValues;
  const day = dayjs().toISOString();
  const patientId = encounter?.subject;

  const serviceRequestResource: any = {
    authoredOn: day,
    resourceType: "ServiceRequest",
    status: "active",
    identifier: [
      {
        value: `${uuidValue}.note.1`,
        system: "urn:ietf:rfc:3986",
      },
    ],
    requisition: {
      value: `${uuidValue}.note`,
      system: "urn:ietf:rfc:3986",
    },
    ...(Array.isArray(basedOn) && basedOn.length > 0 && { basedOn }),
    ...(encounter?.requester && { requester: encounter.requester }),
    subject: {
      reference: `Patient?identifier=urn:oid:${oids.cpf}|${patientId}`,
    },
    encounter: {
      reference: `Encounter/${encounter?.id}`,
    },
    ...(noteText && {
      note: {
        text: noteText,
        author: [
          {
            authorReference: { reference: `Patient/${oids.cpf}-${patientId}` },
          },
        ],
      },
    }),
    intent: "order",
  };

  return JSON.stringify({
    resourceType: "Bundle",
    type: "transaction",
    meta: {
      profile: [
        "http://fluxmed.com.br/fhir/StructureDefinition/ServiceRequestBundle-1.0",
      ],
    },
    entry: [
      {
        resource: serviceRequestResource,
        request: { url: "ServiceRequest", method: "POST" },
      },
    ],
  });
}

function Task(objData: ITaskData): string {
  // Cria um objeto Task base com campos obrigatórios
  const task: Task = {
    resourceType: "Task",
    status: objData.status || "draft",
    intent: objData.intent || "order",
    priority: objData.priority || "routine",
    authoredOn: dayjs().toISOString(),
    lastModified: dayjs().toISOString(),

    // Adiciona campos opcionais diretamente com operador de curto-circuito
    ...(objData.description && { description: objData.description }),
    ...(objData.requester && { requester: { reference: objData.requester } }),
    ...(objData.owner && { owner: { reference: objData.owner } }),
    ...(objData.for && { for: { reference: objData.for } }),
    // Adiciona o campo note se existir
    ...(objData.note && { note: [{ text: objData.note }] }),
    // Adiciona o campo code apenas se existir e tiver texto
    ...(objData.code &&
      objData.code.text &&
      objData.code.text !== "" && { code: objData.code }),
  };

  return JSON.stringify(task);
}

function MediaBinary(objData: any) {
  return {
    resourceType: "Binary",
    contentType: "application/image",
    data: objData.base64,
  };
}

export const auxiliarData = {
  PersonAux,
  Immunization,
  ImmunizationNarrative,
  PhysicalFindingsObservation,
  Practitioner,
  AllergyIntolerance,
  TissDocumentReference,
  Condition,
  CIAP2Condition,
  PractitionerRole,
  PatchPractitionerRole,
  Location,
  HealthcareService,
  DocumentReference,
  DocumentReferenceJson,
  DocumentReferencePatientJson,
  ServiceRequest,
  ServiceRequestNoteOnly,
  MedicationRequest,
  MedicationStatement,
  ProtocolResponse,
  PatientSummary,
  Schedule,
  Patient,
  VetPatient,
  RelatedPerson,
  Appointment,
  Encounter,
  Email,
  Slot,
  OrganizationAffiliation,
  Coverage,
  Patch,
  Task,
  MediaBinary,
};
